<template>
  <div>
    <div class="wrapper">
      <breadcrumb :crumbs="[$t('menu.materials'), $t('text.list')]">
        <el-button
          v-if="Permit.canActions('mmsMaterialManage')"
          type="success"
          size="mini"
          @click="$router.push('/normal/mms/materials/new')"
        >
          + {{ $t("text.create") }}
        </el-button>

        <el-button
          v-if="Permit.canActions('mmsMaterialManage')"
          type="default"
          size="mini"
          @click="$router.push('/normal/mms/materials/import')"
        >
          {{ $t("action.import") }}
        </el-button>
      </breadcrumb>
    </div>
    <narrow
      :narrows="narrows"
      :init-filter="initFilter"
      @narrowSelect="narrowSelect"
    />
    <div class="search with-narrows">
      <el-form
        :inline="true"
        @submit.native.prevent="search"
        :model="searchForm"
        size="small"
      >
        <el-form-item>
          <el-input
            v-model.trim="searchForm.serialNo"
            :placeholder="$t('material.serialNoName')"
            size="small"
          />
        </el-form-item>
        <el-form-item>
          <el-select
            v-model="searchForm.supplierId"
            :placeholder="$t('menu.suppliers')"
            size="small"
            filterable
            clearable
          >
            <el-option
              v-for="supplier in suppliers"
              :key="supplier.id"
              :label="supplier.name"
              :value="supplier.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-input
            v-model.trim="searchForm.supplierMaterialNo"
            :placeholder="$t('material.supplierMaterialNo')"
            size="small"
          />
        </el-form-item>
        <el-form-item>
          <el-input
            size="small"
            v-model.trim="searchForm.minHandCycle"
            :placeholder="$t('material.minHandCycle2')"
            style="width: 100px"
            clearable
          />
        </el-form-item>
        <el-form-item>-</el-form-item>
        <el-form-item>
          <el-input
            size="small"
            v-model.trim="searchForm.maxHandCycle"
            :placeholder="$t('material.maxHandCycle2')"
            style="width: 100px"
            clearable
          />
        </el-form-item>
        <el-form-item>
          <el-input
            v-model.trim="searchForm.remark"
            :placeholder="$t('text.remark')"
            size="small"
          />
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            icon="el-icon-search"
            native-type="submit"
          />
        </el-form-item>
      </el-form>
    </div>
    <div class="wrapper table-area">
      <sticky :sticky-top="50" :viewport="1024" :is-rem="false" class="sticky">
        <vxe-toolbar ref="recordsToolbar" custom zoom class="table-toolbar">
          <template v-slot:buttons>
            <el-button
              type="default"
              size="mini"
              @click="print('printQrCodes')"
            >
              {{ $t("action.printQrCode") }}
            </el-button>
            <el-dropdown class="mgl10 mgr10" @command="download">
              <el-button type="default" size="mini">
                {{ $t("action.download") }}
                <i class="el-icon-caret-bottom el-icon--right" />
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="checked">
                  {{ $t("action.downLoadChecked") }}
                </el-dropdown-item>
                <el-dropdown-item command="search">
                  {{ $t("action.downLoadSearch") }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>

            <el-button type="primary" size="mini" @click="batchSetHandCycle">
              {{ $t("action.batchSetHandCycle") }}
            </el-button>
            <el-button
              type="success"
              size="mini"
              @click="batchSetHandCycleBySupplier"
            >
              {{ $t("action.batchSetHandCycleBySupplier") }}
            </el-button>

            <el-button type="warning" size="mini" @click="materialMap">
              {{ $t("product.variantMapSimple") }}
            </el-button>

            <span class="pager-total">
              {{ $t("rules.pagerTotal", [pager.total]) }}
            </span>
          </template>
        </vxe-toolbar>
      </sticky>

      <vxe-table
        :tree-config="{
          transform: true,
          rowField: 'id',
          parentField: 'parentId',
          expandAll: true,
        }"
        :data="records"
        @checkbox-change="setSelected"
        @checkbox-all="setSelected"
        :custom-config="{ storage: true, checkMethod: checkColumnMethod }"
        :loading="loading"
        id="materialTable"
        row-id="id"
        ref="recordsTable"
        class="recordsTable"
        size="mini"
        resizable
        round
        auto-resize
        highlight-hover-row
        :scroll-y="{ enabled: false }"
      >
        <vxe-table-column
          field="checkbox"
          type="checkbox"
          width="60"
          tree-node
        />
        <vxe-table-column
          field="image"
          :title="$t('product.images')"
          width="80"
        >
          <template v-slot="{ row }">
            <thumbnail :images="row.image" />
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="serialNo"
          :title="$t('material.serialNo')"
          min-width="120"
        >
          <template v-slot="{ row, rowIndex }">
            <span class="blue" @click="showRow(row, rowIndex)">
              {{ row.serialNo }}
            </span>
            <div class="font10">{{ row.name }} {{ row.subjectName }}</div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="color"
          :title="$t('material.color')"
          min-width="80"
        >
          <template v-slot="{ row }">
            {{ row.color }}
            <div class="font10">{{ row.colorNo }}</div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="inUnit"
          :title="$t('material.inUnit')"
          min-width="80"
        />
        <vxe-table-column
          field="unit"
          :title="$t('material.outUnit')"
          min-width="80"
        />
        <vxe-table-column
          field="exchange"
          :title="$t('material.exchange')"
          min-width="70"
        />
        <vxe-table-column
          v-if="Permit.canActionsChargeEdition('globalMaterialPrice')"
          field="inPrice"
          :title="$t('material.inPrice')"
          min-width="90"
        >
          <template v-slot="{ row }">
            {{ row.inPrice | currency4 }}
          </template>
        </vxe-table-column>
        <vxe-table-column
          v-if="Permit.canActionsChargeEdition('globalMaterialPrice')"
          field="price"
          :title="$t('material.outPrice')"
          min-width="90"
        >
          <template v-slot="{ row }">
            {{ row.price | currency4 }}
          </template>
        </vxe-table-column>
        <vxe-table-column
          v-if="Permit.canActionsChargeEdition('mmsMaterialViewPurchaseInfo')"
          field="materialSuppliers"
          :title="$t('menu.suppliers')"
          min-width="200"
        >
          <template v-slot="{ row }">
            <div
              v-for="(
                materialSupplier, materialSuppliersIndex
              ) of row.materialSuppliers"
              :key="materialSuppliersIndex"
              class="font10"
              :class="materialSupplier.defaultSupplier == true ? '' : 'gray'"
            >
              {{ materialSupplier.supplierName }}({{
                materialSupplier.supplierMaterialNo
              }}):{{ materialSupplier.price | currency4 }}
            </div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="price"
          :title="$t('material.handCycle')"
          min-width="80"
        >
          <template v-slot="{ row }">
            {{ row.minHandCycle }}-{{ row.maxHandCycle }}
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="remark"
          :title="$t('text.remark')"
          min-width="90"
        >
          <template v-slot="{ row }">
            <raw-html-limit-number
              :content="row.remark"
              :number="6"
              type="richText"
            />
          </template>
        </vxe-table-column>
        <vxe-table-column
          v-if="Permit.canActionsChargeEdition('mmsMaterialManage')"
          field="action"
          :title="$t('text.action')"
          width="90"
          fixed="right"
        >
          <template v-slot="{ row, rowIndex }">
            <el-button
              type="text"
              size="mini"
              class=""
              @click="edit(row, rowIndex)"
              >{{ $t("text.edit") }}
            </el-button>
            <el-popconfirm
              :title="$t('prompt.confirmDelete')"
              @confirm="deleteRow(row)"
              class="mgl10"
            >
              <el-button slot="reference" type="text" size="mini">
                {{ $t("text.delete") }}
              </el-button>
            </el-popconfirm>
            <div>
              <el-button
                v-if="row.parentMaterialId != null"
                type="text"
                size="mini"
                @click="clearMappingRow(row, rowIndex)"
                >{{ $t("action.clearMapping") }}
              </el-button>
            </div>
          </template>
        </vxe-table-column>
      </vxe-table>
      <div class="flex flex-between pager">
        <div class="selected-qty-tips">
          选中了
          <span class="strong">{{ selectRecordQty }}</span>
          条记录
        </div>
        <div>
          <vxe-pager
            size="small"
            :loading="false"
            :background="false"
            :current-page="pager.currentPage"
            :page-size="pager.pageSize"
            :total="pager.total"
            :layouts="pagerConfig.layouts"
            :page-sizes="pagerConfig.pageSizes"
            @page-change="changePager"
          ></vxe-pager>
        </div>
      </div>
    </div>

    <div id="printQrCodes" style="display: none">
      <div
        v-for="(c, index) in selectRecords"
        :key="index"
        :style="
          'margin:0 auto 10px; width:400px; text-align:left; font-size: 14px;word-break: break-all;word-wrap: break-word' +
          (index == 0 ? '' : 'page-break-before: always;')
        "
      >
        <div style="display: flex">
          <div>
            <qrcode
              :value="c.serialNo"
              :options="{ width: 100 }"
              :tag="'img'"
            />
            <!-- :value="`|||material|${c.serialNo}`" -->
          </div>
          <div
            style="
              margin-top: 8px;
              word-break: break-all;
              word-wrap: break-word;
            "
          >
            <div>{{ $t("material.serialNo") }}：{{ c.serialNo }}</div>
            <div>{{ $t("material.name") }}：{{ c.name }}</div>
            <div>{{ $t("material.subject") }}：{{ c.subjectName }}</div>
            <div v-if="c.supplierName != null">
              {{ $t("menu.suppliers") }}：{{ c.supplierName }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <drawer
      :title="$t('text.edit')"
      :visible.sync="editDF"
      size="900px"
      direction="rtl"
      :wrapper-closable="false"
      :destroy-on-close="true"
    >
      <edit :id="currentRowId" @updateInfo="editUpdateInfo" />
    </drawer>

    <drawer
      :title="$t('text.view')"
      :visible.sync="showDF"
      size="900px"
      direction="rtl"
      :wrapper-closable="false"
      :destroy-on-close="true"
    >
      <show :id="currentRowId" @updateInfo="showRowClose" />
    </drawer>

    <el-dialog
      :title="$t('action.batchSetHandCycle')"
      :close-on-click-modal="false"
      :visible.sync="batchSetHandCycleDF"
      width="30%"
    >
      <el-form
        ref="batchSetHandCycleObjForm"
        :model="batchSetHandCycleObj"
        label-width="120px"
      >
        <el-form-item
          :label="$t('menu.subjects')"
          :prop="'subjectId'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('menu.subjects')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-select size="small" v-model.trim="batchSetHandCycleObj.subjectId">
            <el-option
              v-for="subject in subjects"
              :key="subject.id"
              :label="subject.name"
              :value="subject.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          :label="$t('material.minHandCycle2')"
          :prop="'minHandCycle'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('material.minHandCycle')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-input
            size="small"
            v-model.trim="batchSetHandCycleObj.minHandCycle"
          />
        </el-form-item>
        <el-form-item
          :label="$t('material.maxHandCycle2')"
          :prop="'maxHandCycle'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('material.maxHandCycle')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-input
            size="small"
            v-model.trim="batchSetHandCycleObj.maxHandCycle"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          plain
          :loading="batchSetHandCycleSubmitDoing"
          size="small"
          @click="batchSetHandCycleSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="batchSetHandCycleDF = false">{{
          $t("text.close")
        }}</el-button>
      </div>
    </el-dialog>
    <!-- ////==== -->
    <el-dialog
      :title="$t('action.batchSetHandCycleBySupplier')"
      :close-on-click-modal="false"
      :visible.sync="batchSetHandCycleBySupplierDF"
      width="30%"
    >
      <el-form
        ref="batchSetHandCycleBySupplierObjForm"
        :model="batchSetHandCycleBySupplierObj"
        label-width="120px"
      >
        <el-form-item
          :label="$t('menu.suppliers')"
          :prop="'supplierId'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('menu.suppliers')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-select
            size="small"
            v-model.trim="batchSetHandCycleBySupplierObj.supplierId"
            filterable
          >
            <el-option
              v-for="supplier in suppliers"
              :key="supplier.id"
              :label="supplier.name"
              :value="supplier.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          :label="$t('material.minHandCycle2')"
          :prop="'minHandCycle'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('material.minHandCycle')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-input
            size="small"
            v-model.trim="batchSetHandCycleBySupplierObj.minHandCycle"
          />
        </el-form-item>
        <el-form-item
          :label="$t('material.maxHandCycle2')"
          :prop="'maxHandCycle'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('material.maxHandCycle')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-input
            size="small"
            v-model.trim="batchSetHandCycleBySupplierObj.maxHandCycle"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          plain
          :loading="batchSetHandCycleBySupplierSubmitDoing"
          size="small"
          @click="batchSetHandCycleBySupplierSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button
          size="small"
          @click="batchSetHandCycleBySupplierDF = false"
          >{{ $t("text.close") }}</el-button
        >
      </div>
    </el-dialog>

    <el-dialog
      :title="$t('product.variantMapSimple')"
      :close-on-click-modal="false"
      :visible.sync="materialMapDF"
      width="30%"
    >
      <el-form
        ref="materialMapObjForm"
        :model="materialMapObj"
        label-width="80px"
      >
        <el-form-item
          :label="$t('material.mainMaterial')"
          :prop="'parentMaterialId'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('menu.suppliers')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-select
            size="small"
            v-model="materialMapObj.parentMaterialId"
            filterable
            remote
            :remote-method="querySearchSerialNo"
            :loading="serialNosLoading"
            :clearable="true"
            :placeholder="$t('rules.mustFill', [$t('material.serialNoName')])"
            @change="handleSelectSerialNo"
          >
            <el-option
              v-for="material in materials"
              :key="material.id"
              :label="material.serialNo"
              :value="material.id"
            >
            </el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          plain
          :loading="materialMapSubmimtDoing"
          size="small"
          @click="materialMapSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="materialMapDF = false">{{
          $t("text.close")
        }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>
<script>
import Vue from "vue";
import axios from "axios";
import { page, table } from "@/utils/mixins.js";
import Qrcode from "@chenfengyuan/vue-qrcode";
import RawHtmlLimitNumber from "@/components/RawHtmlLimitNumber";
import Util from "@/utils/index.js";

import SupplierMms from "@/api/supplierMms.js";
import SubjectMms from "@/api/subjectMms.js";
import Material from "@/api/material.js";
import Edit from "./edit";
import Show from "./show";

export default {
  mixins: [
    page,
    table.pager,
    table.search,
    table.narrow,
    table.checker,
    table.sorter,
    table.basic,
  ],
  components: {
    Qrcode,
    RawHtmlLimitNumber,
    Edit,
    Show,
  },
  data() {
    return {
      Util: Util,

      narrows: [
        [
          {
            label: this.$t("text.special"),
            items: [
              {
                label: this.$t("material.serialNoRepeat"),
                key: "serialNoRepeat",
                condition: { special: "serialNoRepeat" },
                numberFun: Material.repeatCount,
                emitNames: ["MaterialUpdate", "MaterialDelete"],
              },
              {
                label: "无供应商",
                key: "noSupplier",
                condition: { special: "noSupplier" },
              },
            ],
          },
          {
            label: "货期",
            items: [
              {
                label: "有货期",
                key: "handCycleYes",
                condition: { handCycle: "yes" },
              },
              {
                label: "无货期",
                key: "handCycleNo",
                condition: { handCycle: "no" },
              },
            ],
          },
        ],
      ],
      initFilter: null,
      searchForm: {
        serialNo: null,
        supplierId: null,
        supplierMaterialNo: null,
        minHandCycle: null,
        maxHandCycle: null,
        remark: null,
      },
      sorter: {
        sortBy: "materials_id",
        sortIndicator: "desc",
      },
      suppliers: [],
      subjects: [],
      //
      currentRowId: null,
      currentRowIdx: null,
      editDF: false,
      //
      currentRowId: null,
      currentRowIdx: null,
      showDF: false,
      //====
      batchSetHandCycleDF: false,
      batchSetHandCycleObj: {
        subjectId: null,
        minHandCycle: null,
        maxHandCycle: null,
      },
      batchSetHandCycleSubmitDoing: false,
      //====
      batchSetHandCycleBySupplierDF: false,
      batchSetHandCycleBySupplierObj: {
        supplierId: null,
        minHandCycle: null,
        maxHandCycle: null,
      },
      batchSetHandCycleBySupplierSubmitDoing: false,
      //映射
      materialMapDF: false,
      materialMapMaterials: [],
      materialMapObj: {
        parentMaterialId: null,
        sunMaterialIds: [],
      },
      materialMapSubmimtDoing: false,
      serialNosLoading: false,
      materials: [],
      clearMappingRowDoing: false,
    };
  },

  created() {
    const self = this;
    this.$nextTick(() => {
      // 手动将表格和工具栏进行关联
      this.$refs.recordsTable.connect(this.$refs.recordsToolbar);
    });
    self.loadingStart();
    axios.all([SubjectMms.findByTenantId(), SupplierMms.findByTenantId()]).then(
      axios.spread(function (subjects, suppliers) {
        if (subjects.status === 200) {
          if (self.Permit.currentUser().admin) {
            self.subjects = subjects.data;
          } else {
            self.subjects = subjects.data.filter((s) =>
              self.Permit.mmsAuthSubjuctIds().includes(s.id)
            );
          }
          self.narrowsSubjects();
        } else {
          self.sysError();
        }
        if (suppliers.status === 200) {
          self.suppliers = suppliers.data;
        } else {
          self.sysError();
        }
        self.loadingEnd();
      })
    );
  },
  methods: {
    clearMappingRow(row) {
      const self = this;
      self.clearMappingRowDoing = true;
      Material.clearMapping(row.id).then(function (res) {
        self.clearMappingRowDoing = false;
        if (res.status === 200) {
          self.$message({
            message: self.$t("prompt.setSuccess"),
            type: "success",
          });
          self.loadData();
        } else {
          self.sysError();
        }
      });
    },
    //映射
    materialMap() {
      const self = this;
      if (self.selectRecords.length == 0) {
        self.$message.error(this.$t("prompt.atLeastOneData"));
        return;
      }
      self.materialMapMaterials = self.selectRecords.filter(
        (r) => r.parentMaterialId == null
      );
      if (self.materialMapMaterials.length == 0) {
        self.$message.error(
          this.$t("prompt.atLeastOneDataCanMaterialMapRecords")
        );
        return;
      }
      self.materialMapSubmimtDoing = false;
      self.materialMapObj.parentMaterialId = null;
      self.materialMapObj.sunMaterialIds = self.materialMapMaterials.map(
        (r) => r.id
      );
      self.materialMapDF = true;
    },
    querySearchSerialNo(queryString, cb) {
      const self = this;
      if (queryString == null || queryString.length < 1) {
        return;
      }
      this.serialNosLoading = true;
      Material.vagueFindBySerialNoOrName({ serialNo: queryString }).then(
        function (res) {
          self.materials = res.data;
          self.serialNosLoading = false;
        }
      );
    },
    handleSelectSerialNo(item) {
      const self = this;
    },
    materialMapSubmit() {
      const self = this;
      this.$refs.materialMapObjForm.validate((valid) => {
        if (valid) {
          self.materialMapObj.sunMaterialIds =
            self.materialMapObj.sunMaterialIds.filter(
              (id) => id != self.materialMapObj.parentMaterialId
            );
          // console.log("====materialMapObj:", self.materialMapObj);
          self.materialMapSubmimtDoing = true;
          Material.mapping(self.materialMapObj).then(function (res) {
            self.materialMapSubmimtDoing = false;
            self.materialMapDF = false;
            if (res.status === 200) {
              self.$message({
                message: self.$t("prompt.setSuccess"),
                type: "success",
              });
              self.loadData();
            } else {
              self.sysError();
            }
          });
        } else {
          return false;
        }
      });
    },
    //
    batchSetHandCycleBySupplier() {
      const self = this;
      self.batchSetHandCycleBySupplierDF = true;
      self.batchSetHandCycleBySupplierSubmitDoing = false;
      self.batchSetHandCycleBySupplierObj.supplierId = null;
      self.batchSetHandCycleBySupplierObj.minHandCycle = null;
      self.batchSetHandCycleBySupplierObj.maxHandCycle = null;
    },
    batchSetHandCycleBySupplierSubmit() {
      const self = this;
      this.$refs.batchSetHandCycleBySupplierObjForm.validate((valid) => {
        if (valid) {
          if (
            self.batchSetHandCycleBySupplierObj.minHandCycle >
            self.batchSetHandCycleBySupplierObj.maxHandCycle
          ) {
            this.$message({
              message: "最大货期必须大于最小货期",
              type: "warning",
            });
            return;
          }
          self.batchSetHandCycleBySupplierObj.minHandCycle = Number(
            self.batchSetHandCycleBySupplierObj.minHandCycle
          );
          self.batchSetHandCycleBySupplierObj.maxHandCycle = Number(
            self.batchSetHandCycleBySupplierObj.maxHandCycle
          );
          self.batchSetHandCycleBySupplierSubmitDoing = true;
          Material.updateHandCycleBySupplier(
            self.batchSetHandCycleBySupplierObj
          ).then(function (res) {
            self.batchSetHandCycleBySupplierSubmitDoing = false;
            self.batchSetHandCycleBySupplierDF = false;
            if (res.status === 200) {
              self.$message({
                message: self.$t("prompt.setSuccess"),
                type: "success",
              });
              self.loadData();
            } else {
              self.sysError();
            }
          });
        } else {
          return false;
        }
      });
    },
    //=======
    batchSetHandCycle() {
      const self = this;
      self.batchSetHandCycleDF = true;
      self.batchSetHandCycleSubmitDoing = false;
      self.batchSetHandCycleObj.subjectId = null;
      self.batchSetHandCycleObj.minHandCycle = null;
      self.batchSetHandCycleObj.maxHandCycle = null;
    },
    batchSetHandCycleSubmit() {
      const self = this;
      this.$refs.batchSetHandCycleObjForm.validate((valid) => {
        if (valid) {
          if (
            self.batchSetHandCycleObj.minHandCycle >
            self.batchSetHandCycleObj.maxHandCycle
          ) {
            this.$message({
              message: "最大货期必须大于最小货期",
              type: "warning",
            });
            return;
          }
          self.batchSetHandCycleObj.minHandCycle = Number(
            self.batchSetHandCycleObj.minHandCycle
          );
          self.batchSetHandCycleObj.maxHandCycle = Number(
            self.batchSetHandCycleObj.maxHandCycle
          );
          self.batchSetHandCycleSubmitDoing = true;
          Material.updateHandCycleBySubject(self.batchSetHandCycleObj).then(
            function (res) {
              self.batchSetHandCycleSubmitDoing = false;
              self.batchSetHandCycleDF = false;
              if (res.status === 200) {
                self.$message({
                  message: self.$t("prompt.setSuccess"),
                  type: "success",
                });
                self.loadData();
              } else {
                self.sysError();
              }
            }
          );
        } else {
          return false;
        }
      });
    },
    //=========
    showRow(row, rowIndex) {
      this.currentRowId = row.id;
      this.currentRowIdx = rowIndex;
      this.showDF = true;
    },
    showRowClose() {
      this.loadData();
    },
    // 编辑
    edit(row, rowIndex) {
      this.currentRowId = row.id;
      this.currentRowIdx = rowIndex;
      this.editDF = true;
    },
    editUpdateInfo(material) {
      for (const key in this.records[this.currentRowIdx]) {
        this.records[this.currentRowIdx][key] = material[key];
      }
      this.editDF = false;
    },
    // 删除
    deleteRow(row) {
      const self = this;
      Material.delete(row.id).then(function (res) {
        if (res.status === 200) {
          self.sysResult(res.data);
          self.loadData();
          self.$eventHub.$emit("MaterialDelete");
        } else {
          self.sysError();
        }
      });
    },

    narrowsSubjects() {
      const self = this;
      self.narrows.push([
        {
          label: self.$t("material.subject"),
          items: self.subjects.map((subject) => {
            return {
              label: subject.name,
              key: subject.id,
              condition: { subjectId: subject.id },
            };
          }),
        },
      ]);
    },

    //
    print(type) {
      const self = this;
      if (self.selectRecords.length === 0) {
        self.$message.error(this.$t("prompt.atLeastOneData"));
        return;
      }
      self.printFunction(type);
    },

    // 下载
    download(type) {
      const self = this;
      if (type == "checked") {
        if (!self.downloadChecked(self.selectRecords)) {
          return;
        }
        const data = {
          currentPage: 1,
          pageSize: self.limitNumber(),
          ids: self.selectRecords.map((c) => c.id),
        };
        self.downloadData(data);
      } else {
        if (!self.downloadSearch(self.pager.total)) {
          return;
        }
        const data = JSON.parse(JSON.stringify(self.combineParams()));
        data.pageSize = self.limitNumber();
        self.downloadData(data);
      }
    },
    downloadData(data) {
      const self = this;
      Material.exportExcel(data).then(function (res) {
        if (res.data.code === 200) {
          self.$message({
            message: self.$t("prompt.downloadPrompt"),
            type: "success",
          });
          self.loadData();
          self.$eventHub.$emit("emitDownLoad");
        } else {
          self.$message.error(self.$t("prompt.downloadErr"));
        }
      });
    },
    //
    dataProvider(params) {
      return Material.search(params);
    },
    recordsDecorate(records) {
      const self = this;
      for (const record of records) {
        Vue.set(record, "parentId", record.parentMaterialId);
      }
      self.records = records;
    },
    calculateBgColor({ row, rowIndex }) {
      return row.parentMaterialId == null ? "light-blue-bg" : "";
    },
  },
};
</script>
